import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Users, Loader2, Check, X, UserPlus, Ear, UserCheck, Headset, Clapperboard, Power, PowerOff, ChevronDown } from "lucide-react";
import { useFeatureFlags } from "@/lib/featureFlags";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import { EmptyState, SectionHeader } from "@/components/PageHeader";

function ManageMembersDialog({ group, open, setOpen, onDone }) {
  const [roster, setRoster] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !group) return;
    setLoading(true); setQ("");
    api.get("/coach/team-members").then(({ data }) => {
      const items = (data.items || []).filter((m) => m.is_active !== false);
      items.sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
      setRoster(items);
      setSel(new Set(items.filter((m) => (m.subgroup_ids || []).includes(group.id)).map((m) => m.id)));
    }).catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, group]);

  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const filtered = roster.filter((m) => `${m.first_name} ${m.last_name}`.toLowerCase().includes(q.toLowerCase()));
  const allVisibleSelected = filtered.length > 0 && filtered.every((m) => sel.has(m.id));
  const toggleAll = () => setSel((p) => {
    const n = new Set(p);
    if (allVisibleSelected) filtered.forEach((m) => n.delete(m.id));
    else filtered.forEach((m) => n.add(m.id));
    return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/coach/subgroups/${group.id}/members`, { member_ids: [...sel] });
      toast.success(`Updated ${group.name} membership`); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-hidden" data-testid="manage-members-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Manage members{group ? ` — ${group.name}` : ""}</DialogTitle>
          <DialogDescription>Select the team members who belong to this group. Changes apply when you save.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : roster.length === 0 ? (
          <EmptyState title="No members yet" description="Approve team members first, then assign them here." testId="manage-members-empty" />
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" data-testid="manage-members-search" />
              <Button type="button" variant="outline" size="sm" className="shrink-0 rounded-full" onClick={toggleAll} data-testid="manage-members-select-all">
                {allVisibleSelected ? "Clear" : "Select all"}
              </Button>
            </div>
            <div className="mt-2 max-h-[45vh] space-y-1 overflow-y-auto pr-1" data-testid="manage-members-roster">
              {filtered.map((m) => {
                const active = sel.has(m.id);
                return (
                  <button key={m.id} type="button" onClick={() => toggle(m.id)} data-testid={`manage-member-${m.id}`}
                    className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left text-sm transition-colors ${active ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300"}`}>
                      {active && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className="font-medium text-slate-800">{m.first_name} {m.last_name}</span>
                  </button>
                );
              })}
              {filtered.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No members match your search.</p>}
            </div>
            <p className="mt-1 text-xs text-slate-400">{sel.size} selected</p>
          </>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={saving || loading} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="manage-members-save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Deactivated groups: hidden everywhere else, kept here so the coach can bring them back.
function InactiveGroups({ items, onReactivate, onDelete }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div className="mt-8 max-w-2xl" data-testid="inactive-groups">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500" aria-expanded={open} data-testid="inactive-groups-toggle">
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /> Inactive groups ({items.length})
      </button>
      {open && (
        <div className="mt-3 grid gap-2" data-testid="inactive-groups-list">
          {items.map((g) => (
            <Card key={g.id} className="flex flex-wrap items-center justify-between gap-3 border-dashed border-slate-300 bg-slate-50 p-3" data-testid={`inactive-group-row-${g.id}`}>
              <div className="flex items-center gap-3 opacity-70">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200"><Users className="h-4 w-4 text-slate-500" /></span>
                <div>
                  <p className="font-semibold text-slate-700">{g.name}</p>
                  <p className="text-xs text-slate-400">{g.member_count} member{g.member_count === 1 ? "" : "s"} · Deactivated</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => onReactivate(g)} data-testid={`group-reactivate-${g.id}`}><Power className="mr-1 h-3.5 w-3.5" /> Reactivate</Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" title="Delete" onClick={() => onDelete(g)} data-testid={`inactive-group-delete-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CoachGroups() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [del, setDel] = useState(null);
  const [manage, setManage] = useState(null);
  const { attendance: attendanceOn, show_notes: showNotesOn } = useFeatureFlags();

  const load = useCallback(async () => {
    const { data } = await api.get("/coach/subgroups", { params: { include_inactive: true } });
    setItems(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const [deact, setDeact] = useState(null);
  const active = items.filter((g) => g.is_active !== false);
  const inactive = items.filter((g) => g.is_active === false);

  const setActive = async (g, val) => {
    try {
      await api.put(`/coach/subgroups/${g.id}`, { is_active: val });
      toast.success(val ? `${g.name} reactivated` : `${g.name} deactivated`); setDeact(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDeact(null); }
  };

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a group name"); return; }
    setCreating(true);
    try {
      await api.post("/coach/subgroups", { name: name.trim() });
      toast.success("Group created"); setName(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const saveRename = async (id) => {
    if (!editName.trim()) { toast.error("Enter a group name"); return; }
    try {
      await api.put(`/coach/subgroups/${id}`, { name: editName.trim() });
      toast.success("Group renamed"); setEditId(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleAnnounce = async (g, val) => {
    setItems((prev) => prev.map((x) => x.id === g.id ? { ...x, can_announce: val } : x));
    try {
      await api.put(`/coach/subgroups/${g.id}`, { can_announce: val });
      toast.success(val ? `${g.name} can now draft announcements` : `${g.name} can no longer draft announcements`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const toggleAttendance = async (g, val) => {
    setItems((prev) => prev.map((x) => x.id === g.id ? { ...x, can_take_attendance: val } : x));
    try {
      await api.put(`/coach/subgroups/${g.id}`, { can_take_attendance: val });
      toast.success(val ? `${g.name} can now take attendance` : `${g.name} can no longer take attendance`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const toggleFlag = async (g, key, val, msg) => {
    setItems((prev) => prev.map((x) => x.id === g.id ? { ...x, [key]: val } : x));
    try {
      await api.put(`/coach/subgroups/${g.id}`, { [key]: val });
      toast.success(msg);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/coach/subgroups/${del.id}`);
      toast.success("Group deleted"); setDel(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  return (
    <div>
      <SectionHeader subtitle="Create subgroups (Varsity, JV, Officers…). Use “Manage members” to assign several members at once." testId="groups-section-header" />

      <Card className="mb-6 max-w-lg border-slate-200 p-4 shadow-sm" data-testid="create-group-card">
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">New group</label>
        <div className="mt-2 flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder="e.g. Varsity" data-testid="group-name-input" />
          <Button onClick={create} disabled={creating} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="group-create-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      </Card>

      {active.length === 0 ? (
        <EmptyState title="No groups yet" description="Create your first subgroup above." testId="groups-empty" />
      ) : (
        <div className="grid max-w-2xl gap-2" data-testid="groups-list">
          {active.map((g) => (
            <Card key={g.id} className="flex flex-wrap items-center justify-between gap-3 border-slate-200 p-3 shadow-sm" data-testid={`group-row-${g.id}`}>
              {editId === g.id ? (
                <div className="flex flex-1 items-center gap-2">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveRename(g.id)} autoFocus data-testid={`group-edit-input-${g.id}`} />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => saveRename(g.id)} data-testid={`group-save-${g.id}`}><Check className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <span className={`flex h-9 w-9 items-center justify-center rounded-full ${g.is_stage_manager ? "bg-rose-100" : "bg-slate-100"}`}>
                      {g.is_stage_manager ? <Headset className="h-4 w-4 text-rose-600" /> : <Users className="h-4 w-4 text-slate-600" />}
                    </span>
                    <div>
                      <p className="font-semibold text-slate-800">{g.name}</p>
                      <p className="text-xs text-slate-400">{g.member_count} member{g.member_count === 1 ? "" : "s"}{g.is_stage_manager && g.name !== "Stage Manager" && <span className="text-rose-600" data-testid={`group-stage-manager-badge-${g.id}`}> · Stage Manager group</span>}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    {showNotesOn && g.is_stage_manager && (
                      <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-500" title="Members of this group can draft Rehearsal and Show Notes for your approval" data-testid={`group-notes-label-${g.id}`}>
                        <Clapperboard className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Can draft Rehearsal &amp; Show Notes</span>
                        <Switch checked={g.can_draft_notes !== false} data-testid={`group-notes-toggle-${g.id}`}
                          onCheckedChange={(v) => toggleFlag(g, "can_draft_notes", !!v, v ? `${g.name} can now draft Rehearsal & Show Notes` : `${g.name} can no longer draft Rehearsal & Show Notes`)} />
                      </label>
                    )}
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-500" title="Members of this group can draft Rally Call messages for your approval">
                      <Ear className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{g.is_stage_manager ? "Can draft Rally Call" : "Can draft"}</span>
                      <Switch checked={!!g.can_announce} onCheckedChange={(v) => toggleAnnounce(g, !!v)} data-testid={`group-announce-toggle-${g.id}`} />
                    </label>
                    {attendanceOn && (
                      <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-500" title="Members of this group can take attendance on Daily Schedule events" data-testid={`group-attendance-label-${g.id}`}>
                        <UserCheck className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Can take attendance</span>
                        <Switch checked={!!g.can_take_attendance} onCheckedChange={(v) => toggleAttendance(g, !!v)} data-testid={`group-attendance-toggle-${g.id}`} />
                      </label>
                    )}
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setManage(g)} data-testid={`group-manage-${g.id}`}><UserPlus className="mr-1 h-3.5 w-3.5" /> Manage members</Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditId(g.id); setEditName(g.name); }} data-testid={`group-rename-${g.id}`}><Pencil className="h-4 w-4" /></Button>
                      {!g.is_stage_manager && <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600" title="Deactivate" onClick={() => setDeact(g)} data-testid={`group-deactivate-${g.id}`}><PowerOff className="h-4 w-4" /></Button>}
                      {!g.is_stage_manager && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" title="Delete" onClick={() => setDel(g)} data-testid={`group-delete-${g.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      )}

      <InactiveGroups items={inactive} onReactivate={(g) => setActive(g, true)} onDelete={setDel} />

      <ManageMembersDialog group={manage} open={!!manage} setOpen={(o) => !o && setManage(null)} onDone={load} />

      <ConfirmDialog
        open={!!deact} onOpenChange={(o) => !o && setDeact(null)}
        title="Deactivate this group?"
        description={deact ? `"${deact.name}" will be hidden from the group list, member badges and Send To lists. Its ${deact.member_count} member assignment(s) and past posts are kept, and you can reactivate it anytime.` : ""}
        confirmText="Deactivate" onConfirm={() => setActive(deact, false)} testId="group-deactivate-confirm"
      />

      <ConfirmDialog
        open={!!del} onOpenChange={(o) => !o && setDel(null)}
        title="Delete this group?"
        description={del ? `"${del.name}" will be removed and unassigned from all ${del.member_count} member(s) and any events targeting it. Members and events themselves are not deleted.` : ""}
        confirmText="Delete" destructive onConfirm={doDelete} testId="group-delete-confirm"
      />
    </div>
  );
}
